import { Vector3 } from "three";
import { Context } from "../../context";
import {
  ActorOfInterest,
  DefensivePlayMode,
  DefensivePlayState,
  LocationOfInterest,
  OffensivePlayMode,
  OffensivePlayState,
  PlayStateManagerCb,
  PositionOfInterest,
} from "../../managers/playstate";
import { ControllerStrategy, CameraModeOptions } from "../strategy/strategy";
import {
  CameraLandmark,
  CameraLandmarkLookup,
  CameraMode,
  SmoothTimes,
} from "../controller";
import { OfftrackRevertControllerStrategy } from "./action";
import {
  PoserPlayerType,
  getPoserPlayerType,
} from "../../../lib/misc/player-type";

const defaultCameraModeOptions: CameraModeOptions = {
  focusedID: 1,
};

export class PersonalControllerStrategy extends ControllerStrategy {
  private person?: ActorOfInterest = undefined;
  private target?: LocationOfInterest = undefined;
  private homePos?: Vector3 = undefined;
  private initialised = false;
  private focusedID: number = 1;
  private playerType: PoserPlayerType = PoserPlayerType.PITCHER;
  private prevFOV: number = 50;

  private defaultAutoTrack: boolean = false;
  private defaultDamping: number = SmoothTimes.MEDIUM;

  private POIs: [
    PositionOfInterest | undefined,
    PositionOfInterest | undefined
  ] = [undefined, undefined];

  private stateUpdateCb?: PlayStateManagerCb;

  init(
    context: Context,
    options: CameraModeOptions = defaultCameraModeOptions
  ): ControllerStrategy {
    super.init(context);
    this.prevFOV = context.camera.fov;
    this.focusedID = options.focusedID || 1;
    this.playerType = getPoserPlayerType(this.focusedID);

    context.state.nameplatePositions.add(this.focusedID);

    if (!this.registered) {
      console.error("no controller registered for personal camera strategy");
      return this;
    }

    if (this.registered.controls) {
      this.defaultDamping = this.registered.controls.smoothTime;
    }

    this.defaultAutoTrack = this.registered.state.autoTrack;

    this.registered.addAuxStrategy(OfftrackRevertControllerStrategy);
    this.registered.state.autoTrack = false;

    this.person = new ActorOfInterest(context, this.focusedID);

    // Getting current play in init may cause issues with
    // position switching handedness while live
    switch (this.playerType) {
      case PoserPlayerType.PITCHER:
        if (context.gameState.getPitchHand() == "Left") {
          this.homePos =
            CameraLandmarkLookup[CameraLandmark.MOUND_LEFT].clone();
          this.target = new LocationOfInterest(
            context,
            CameraLandmark.PITCHER_POV_L
          );
        } else {
          this.homePos =
            CameraLandmarkLookup[CameraLandmark.MOUND_RIGHT].clone();
          this.target = new LocationOfInterest(
            context,
            CameraLandmark.PITCHER_POV_R
          );
        }

        break;
      case PoserPlayerType.CATCHER: //catcher falls through to batter
      case PoserPlayerType.BATTER:
        if (context.gameState.getBatHand() == "Left") {
          this.homePos = CameraLandmarkLookup[CameraLandmark.UMP_LEFT].clone();
          this.target = new LocationOfInterest(
            context,
            CameraLandmark.BATTER_TARGET_L
          );
        } else {
          this.homePos = CameraLandmarkLookup[CameraLandmark.UMP_RIGHT].clone();
          this.target = new LocationOfInterest(
            context,
            CameraLandmark.BATTER_TARGET_R
          );
        }
        break;
      default:
        let loc =
          this.person.position || CameraLandmarkLookup[CameraLandmark.HH];
        this.homePos?.addVectors(
          loc,
          loc.clone().normalize().multiplyScalar(25)
        );
        this.homePos?.setY(7);
        this.target = new LocationOfInterest(
          context,
          CameraLandmark.PITCHER_PLATE_MIDPOINT
        );
        break;
    }

    this.registered.state.homePos = this.homePos;
    this.POIs[0] = this.person;
    this.POIs[1] = undefined;
    this.registered.contextRef.camera.updateProjectionMatrix();

    return this;
  }

  update(deltaS: number, gameTimeDelta: number, frameNum?: number) {
    if (!this.registered) {
      console.error("no controls registered");
      return;
    }

    super.update(deltaS, gameTimeDelta, frameNum);

    // lazy register for play state updates here because if we do it in init, the callback
    // triggering steps on itself (setMode gets called simultaneously in different threads)
    if (!this.initialised) {
      // play state callback
      this.stateUpdateCb = (
        defense: DefensivePlayState,
        offense: OffensivePlayState
      ) => {
        this.playStateUpdated(offense, defense);
      };

      this.registered.setMode(CameraMode.PAIR, { fromManager: true });

      const playStateManager = this.registered.contextRef.playStateManager;
      playStateManager?.addSubscriber("personal-cam", this.stateUpdateCb);

      this.initialised = true;
    }
    this.registered.state.tracked = this.POIs[0]?.object;
    this.registered.state.trackedOther = this.POIs[1]?.object;

    if (frameNum != undefined) {
      this.lastFrameNum = frameNum;
    }
  }

  playStateUpdated(offense: OffensivePlayState, defense: DefensivePlayState) {
    const controller = this.registered;
    if (!controller || !controller.controls) return;

    switch (offense.mode) {
      case OffensivePlayMode.IDLE:
        controller.controls.smoothTime = SmoothTimes.SLOW;
        controller.controls.zoomTo(1, true);
        controller.contextRef.camera.fov = 45;
        this.POIs[0] = this.person;
        this.POIs[1] = undefined;
        break;

      case OffensivePlayMode.BATTING:
        controller.controls.smoothTime = SmoothTimes.FAST;
        if (this.playerType == PoserPlayerType.PITCHER) {
          if (controller.camera.aspect < 1.0) {
            controller.controls?.zoomTo(2.5, true);
            controller.contextRef.camera.fov = 50;
          } else {
            controller.controls?.zoomTo(3.5, true);
            controller.contextRef.camera.fov = 45;
          }
        }
        if (
          this.playerType == PoserPlayerType.PITCHER ||
          this.playerType == PoserPlayerType.CATCHER ||
          this.playerType == PoserPlayerType.BATTER
        ) {
          controller.controls.smoothTime = SmoothTimes.VERY_FAST;
          this.POIs[0] = this.target;
          this.POIs[1] = undefined;
        } else {
          this.POIs[0] = this.person;
          this.POIs[1] = this.target;
        }
        break;

      case OffensivePlayMode.RUNNING: {
        controller.controls.zoomTo(1, true);
        controller.contextRef.camera.fov = 45;
        controller.controls.smoothTime = SmoothTimes.FAST;
        this.POIs[0] = this.person;
        this.POIs[1] = offense.ballOfInterest;
        break;
      }

      default:
        console.error("unsupported game state in batter cam");
        break;
    }

    if (
      this.playerType == PoserPlayerType.FIELDER ||
      this.playerType == PoserPlayerType.RUNNER
    ) {
      switch (defense.mode) {
        case DefensivePlayMode.FIELDING:
        case DefensivePlayMode.THROWING:
          if (defense.posOfInterest.secondary instanceof ActorOfInterest) {
            if (defense.posOfInterest.secondary.id != this.focusedID) {
              this.POIs[1] = defense.posOfInterest.secondary;
            }
          }
          break;
      }
    }
  }

  teardown(): void {
    super.teardown();

    if (!this.registered) return;

    if (this.registered?.controls) {
      this.registered.controls.enabled = true;
      this.registered.controls.smoothTime = this.defaultDamping;
      this.registered.controls.zoomTo(1, true);
    }

    this.registered.state.tracked = undefined;
    this.registered.state.trackedOther = undefined;

    this.registered.removeAuxStrategy(OfftrackRevertControllerStrategy);
    this.registered.state.autoTrack = this.defaultAutoTrack;

    const playStateManager = this.registered.contextRef.playStateManager;
    playStateManager?.removeSubscriber("personal-cam");

    if (this.focusedID != 1 && this.focusedID != 10) {
      this.registered.contextRef.state.nameplatePositions.delete(
        this.focusedID
      );
    }
    this.registered.contextRef.camera.fov = this.prevFOV;
  }
}
